import axiosInstance from './axiosInstance';

export type GmailInviteBatchStatus =
  | 'collecting' | 'ready' | 'sending' | 'completed' | 'stopped' | 'failed' | 'discarded' | 'interrupted';

export type InviteRole = 'reader' | 'writer';

/** Where invites go: one of the user's projects with an access level, or (projectId null) 8080.AI itself. */
export interface InviteTarget {
  projectId: string | null;
  role: InviteRole;
}

export interface GmailInviteBatch {
  id: string;
  status: GmailInviteBatchStatus;
  mode: 'send' | 'review';
  dry_run: boolean;
  /** True while the contact list is held (one hour) for the user to choose from. */
  choosing: boolean;
  sender_email: string | null;
  project_id: string | null;
  role: InviteRole | null;
  contacts_read: number;
  /** Everyone the user could invite (not opted out). */
  contacts_found: number;
  invite_count: number;
  sent_count: number;
  built_count: number;
  failed_count: number;
  error: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface InvitePerson {
  email: string;
  name: string | null;
  /** ``contact`` = saved contacts, ``other_contact`` = people the user emailed or replied to. */
  source: 'contact' | 'other_contact' | null;
  status: string;
  /** Already has an 8080.AI account: can be invited to a project, not to 8080.AI. */
  member: boolean;
  /** This user already invited them to 8080.AI. */
  invited: boolean;
}

export interface InvitePeoplePage {
  total: number;
  page: number;
  page_size: number;
  people: InvitePerson[];
  /** The addresses "invite the first N" ticks, in order (up to 200). */
  first: string[];
  choosing: boolean;
}

export interface InviteProject {
  id: string;
  title: string;
  /** Room left on the owner's plan for members and pending invitations; null = unlimited. */
  seats_left: number | null;
  updated_at: string | null;
}

export interface GmailInviteStatus {
  available: boolean;
  reason: string | null;
  dry_run: boolean;
  max_invites: number;
  batch_sizes: number[];
  default_batch_size: number;
  remaining_today: number;
  latest_batch: GmailInviteBatch | null;
}

export interface GmailInvitePreview {
  from: string;
  to: string | null;
  subject: string;
  html: string;
  text: string;
  /** The words without markup, for showing the email inside the app. */
  parts?: { greeting: string; pitch: string; button: string; signature: string; footer: string[] } | null;
}

export type GmailInviteSendResult = { started: true } | { reconnect_url: string };

export interface GmailInviteFunnelSteps {
  sent: number;
  opened: number;
  clicked: number;
  registered: number;
  paid: number;
}

export interface GmailInviteAnalytics {
  start_at: string;
  end_at: string;
  include_dry_runs: boolean;
  totals: GmailInviteFunnelSteps & {
    batches: number;
    inviters: number;
    contacts_found: number;
    failed: number;
    unsubscribed: number;
  };
  daily: Array<GmailInviteFunnelSteps & { date: string }>;
  batches: Array<GmailInviteFunnelSteps & {
    id: string;
    created_at: string;
    inviter_id: string;
    inviter_name: string | null;
    status: GmailInviteBatchStatus;
    mode: 'send' | 'review';
    dry_run: boolean;
    contacts_found: number;
    failed: number;
  }>;
}

const BASE = '/api/v1/gmail-invites';

export const BUSY_STATUSES: ReadonlySet<GmailInviteBatchStatus> = new Set<GmailInviteBatchStatus>(['collecting', 'sending']);
export const INVITE_TOKEN_KEY = 'gmail_invite_token';

const targetParams = (target: InviteTarget) => (
  target.projectId ? { project_id: target.projectId, role: target.role } : {}
);

interface PeopleQuery {
  q: string;
  page: number;
  pageSize: number;
  general: boolean;
}

export const gmailInviteService = {
  async status(): Promise<GmailInviteStatus> {
    return (await axiosInstance.get<GmailInviteStatus>(`${BASE}/status`)).data;
  },

  async projects(): Promise<InviteProject[]> {
    return (await axiosInstance.get<{ projects: InviteProject[] }>(`${BASE}/projects`)).data.projects;
  },

  async connect(returnTo: string, showConsent = false): Promise<string> {
    const response = await axiosInstance.post<{ authorization_url: string }>(
      `${BASE}/connect`, { return_to: returnTo, show_consent: showConsent },
    );

    return response.data.authorization_url;
  },

  async batch(batchId: string): Promise<GmailInviteBatch> {
    return (await axiosInstance.get<GmailInviteBatch>(`${BASE}/batches/${batchId}`)).data;
  },

  async people(batchId: string, query: PeopleQuery): Promise<InvitePeoplePage> {
    const params = { q: query.q, page: query.page, page_size: query.pageSize, general: query.general };

    return (await axiosInstance.get<InvitePeoplePage>(`${BASE}/batches/${batchId}/people`, { params })).data;
  },

  async samplePreview(target: InviteTarget): Promise<GmailInvitePreview> {
    return (await axiosInstance.get<GmailInvitePreview>(`${BASE}/preview`, { params: targetParams(target) })).data;
  },

  async preview(batchId: string, email: string | null, target: InviteTarget): Promise<GmailInvitePreview> {
    const params = { ...targetParams(target), ...(email ? { email } : {}) };

    return (await axiosInstance.get<GmailInvitePreview>(`${BASE}/batches/${batchId}/preview`, { params })).data;
  },

  async send(
    batchId: string, emails: string[], target: InviteTarget, returnTo: string,
  ): Promise<GmailInviteSendResult> {
    const response = await axiosInstance.post<GmailInviteSendResult>(`${BASE}/batches/${batchId}/send`, {
      emails,
      return_to: returnTo,
      project_id: target.projectId,
      role: target.role,
    });

    return response.data;
  },

  async discard(batchId: string): Promise<void> {
    await axiosInstance.post(`${BASE}/batches/${batchId}/discard`);
  },

  async claim(token: string): Promise<boolean> {
    return (await axiosInstance.post<{ counted: boolean }>(`${BASE}/claim`, { token })).data.counted;
  },

  async analytics(days: number, includeDryRuns: boolean): Promise<GmailInviteAnalytics> {
    const response = await axiosInstance.get<GmailInviteAnalytics>('/api/v1/admin/gmail-invites/analytics', {
      params: { days, include_dry_runs: includeDryRuns },
    });

    return response.data;
  },
};
